import React, { useCallback, useEffect, useRef, useState } from 'react';
import { UseOtpResult } from './useOtp';
import { StandaloneOtpInput } from '@ianesthesie-kit/otp-input';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Tooltip
} from '@mui/material';
import { DoubleArrowCircleIcon } from '@/components/icon';
import { useErrorNudgeWrapper } from '@ianesthesie-kit/motion';
import { OTP_TTL } from '@/config';
import { useTranslator } from '@ianesthesie-kit/core';
import SendIcon from '@mui/icons-material/Send';

type OtpInputWithControlsProps = Omit<UseOtpResult, 'requestOtp'> & {
  onRequestOtp: () => void;
  autoSubmit?: boolean;
  initialOtpSentAutomatically?: boolean;
};

export const OtpInputWithControls = ({
  otp,
  onChange,
  isOtpPending,
  otpError,
  verifyOtp,
  onRequestOtp,
  countdown,
  isCountdownRunning,
  disabled,
  autoSubmit = true,
  isSubmitting,
  initialOtpSentAutomatically
}: OtpInputWithControlsProps) => {
  const translate = useTranslator();
  const { animate, NudgeWrapper } = useErrorNudgeWrapper();
  const errorAnimationRan = useRef(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [sentOnce, setSentOnce] = useState(
    !!initialOtpSentAutomatically || isOtpPending
  );

  const requestOtp = useCallback(() => {
    setSentOnce(true);
    onRequestOtp();
  }, [onRequestOtp]);

  const onComplete = (code: string) => {
    if (autoSubmit) {
      verifyOtp(code);
    } else {
      setCanSubmit(true);
    }
  };

  const onSubmitClick = () => {
    if (canSubmit) {
      verifyOtp(otp);
    }
  };

  useEffect(() => {
    if (otpError) {
      setCanSubmit(false);
      if (!errorAnimationRan.current) {
        animate().then(() => (errorAnimationRan.current = true));
      }
    } else {
      errorAnimationRan.current = false;
    }
  }, [otpError, animate]);

  return (
    <Grid container columnSpacing={2} rowGap={2} alignItems="center">
      {!sentOnce && (
        <Grid item xs={12}>
          <Button variant="text" onClick={requestOtp}>
            Request a code
          </Button>
        </Grid>
      )}
      {sentOnce && (
        <>
          <Grid item xs={12} sm="auto">
            <NudgeWrapper>
              <StandaloneOtpInput
                value={otp}
                onChange={onChange}
                disabled={disabled}
                error={!!otpError}
                onComplete={onComplete}
                loading={isSubmitting}
              />
            </NudgeWrapper>
          </Grid>
          <Grid item xs={12} sm={'auto'}>
            <AnimatePresence mode="wait">
              <Stack
                direction={'row'}
                component={motion.div}
                key={isCountdownRunning ? 'running' : 'notRunning'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                alignItems="center"
                justifyContent="center"
                width="fit-content"
              >
                {isCountdownRunning && (
                  <CircularProgress
                    size={20}
                    thickness={5}
                    color="inherit"
                    variant="determinate"
                    value={(countdown / OTP_TTL) * 100}
                    sx={{
                      mx: 'auto'
                    }}
                  />
                )}
                {!isCountdownRunning && (
                  <Tooltip
                    title={translate('app.auth.register.otp.resendButton')}
                  >
                    <IconButton
                      component={motion.button}
                      onClick={requestOtp}
                      size="small"
                      sx={{ p: 0.5, backgroundColor: 'transparent!important' }}
                      whileHover={{ rotate: 180 }}
                    >
                      <DoubleArrowCircleIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {!autoSubmit && (
                  <IconButton
                    size="small"
                    component={motion.button}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    whileHover={{ scale: 1.05 }}
                    color="primary"
                    sx={{ ml: 1.5 }}
                    disabled={!canSubmit}
                    onClick={onSubmitClick}
                  >
                    <SendIcon />
                  </IconButton>
                )}
              </Stack>
            </AnimatePresence>
          </Grid>
        </>
      )}
    </Grid>
  );
};
